import { useMemo, useState, type ReactElement } from 'react';
import { SmilePlus, X } from 'lucide-react';
import type { CommentReactionList, ReactionEmoji } from '@molt/shared';

import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { IconButton } from '@/components/IconButton';
import { Popover } from '@/components/Popover';
import { TextField } from '@/components/TextField';
import { Tooltip } from '@/components/Tooltip';
import { profileName } from '@/components/Avatar';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

export interface CommentReactionsProps {
  readonly commentId: string;
  readonly reactions: CommentReactionList;
  readonly canReact: boolean;
  readonly canRemoveOwnReaction?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly onAdd: (commentId: string, emoji: ReactionEmoji) => void;
  readonly onRemove: (commentId: string, emoji: ReactionEmoji) => void;
}

const EMOJI_OPTIONS = [
  '👍',
  '❤️',
  '😂',
  '🎉',
  '😮',
  '😢',
  '🙏',
  '👀',
  '✅',
  '🔥',
] as const;

const RECENT_REACTIONS_KEY = 'molt:comment-recent-reactions';
const RECENT_REACTION_LIMIT = 6;
type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export function CommentReactions({
  commentId,
  reactions,
  canReact,
  canRemoveOwnReaction = canReact,
  busy = false,
  onAdd,
  onRemove,
}: CommentReactionsProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<readonly ReactionEmoji[]>(() =>
    readRecentReactions(),
  );
  const reacted = useMemo(
    () =>
      new Set(
        reactions
          .filter((reaction) => reaction.reactedByMe)
          .map((reaction) => reaction.emoji),
      ),
    [reactions],
  );
  const trimmedQuery = query.trim().toLocaleLowerCase();
  const options = EMOJI_OPTIONS.filter((emoji) => {
    if (trimmedQuery === '') return true;
    return (
      emoji.includes(trimmedQuery) ||
      emojiSearchAlias(t, emoji).toLocaleLowerCase().includes(trimmedQuery)
    );
  });
  const recentOptions = recent.filter((emoji) =>
    EMOJI_OPTIONS.includes(emoji as (typeof EMOJI_OPTIONS)[number]),
  );

  if (!canReact && !canRemoveOwnReaction && reactions.length === 0) return null;

  function selectEmoji(emoji: ReactionEmoji): void {
    setRecent(rememberRecentReaction(emoji));
    onAdd(commentId, emoji);
    setOpen(false);
    setQuery('');
  }

  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-1.5"
      aria-label={t('cardBack.comments.reactions.region')}
    >
      {reactions.map((reaction) => {
        const names = reaction.users.map((user) => profileName(user));
        const tooltip =
          names.length === 0
            ? t(
                'cardBack.comments.reactions.count',
                { count: reaction.count },
                reaction.count,
              )
            : names.join(', ');
        const chipRemovesOwnReaction = reaction.reactedByMe && canRemoveOwnReaction;
        const chipAddsReaction = !reaction.reactedByMe && canReact;
        return (
          <Tooltip key={reaction.emoji} content={tooltip}>
            {chipRemovesOwnReaction || chipAddsReaction ? (
              <button
                type="button"
                className={cn(
                  'inline-flex h-7 items-center gap-1 rounded-full border border-border bg-surface-raised px-2 text-sm text-ink shadow-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60',
                  reaction.reactedByMe && 'border-accent bg-accent-soft text-accent',
                )}
                disabled={busy}
                aria-pressed={reaction.reactedByMe}
                aria-label={
                  chipRemovesOwnReaction
                    ? t('cardBack.comments.reactions.removeEmoji', {
                        emoji: reaction.emoji,
                      })
                    : t('cardBack.comments.reactions.addEmoji', {
                        emoji: reaction.emoji,
                      })
                }
                onClick={() => {
                  if (chipRemovesOwnReaction) onRemove(commentId, reaction.emoji);
                  else onAdd(commentId, reaction.emoji);
                }}
              >
                <span aria-hidden="true">{reaction.emoji}</span>
                <span>{reaction.count}</span>
              </button>
            ) : (
              <span
                className={cn(
                  'inline-flex h-7 items-center gap-1 rounded-full border border-border bg-surface-raised px-2 text-sm text-ink shadow-card',
                  reaction.reactedByMe && 'border-accent bg-accent-soft text-accent',
                )}
                role="img"
                aria-label={t(
                  'cardBack.comments.reactions.countLabel',
                  { emoji: reaction.emoji, count: reaction.count },
                  reaction.count,
                )}
              >
                <span aria-hidden="true">{reaction.emoji}</span>
                <span>{reaction.count}</span>
              </span>
            )}
          </Tooltip>
        );
      })}
      {canReact ? (
        <Popover
          open={open}
          onOpenChange={setOpen}
          title={t('cardBack.comments.reactions.add')}
          width={260}
          trigger={
            <IconButton
              label={t('cardBack.comments.reactions.add')}
              size="sm"
              disabled={busy}
            >
              <SmilePlus aria-hidden="true" className="size-4" />
            </IconButton>
          }
          closeControl={
            <IconButton label={t('cardBack.comments.reactions.closePicker')} size="sm">
              <X aria-hidden="true" className="size-4" />
            </IconButton>
          }
          search={
            <TextField
              label={t('cardBack.comments.reactions.search')}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              wrapperClassName="gap-1"
            />
          }
        >
          {recentOptions.length > 0 && trimmedQuery === '' ? (
            <EmojiGrid
              label={t('cardBack.comments.reactions.recentHeading')}
              options={recentOptions}
              reacted={reacted}
              onSelect={selectEmoji}
            />
          ) : null}
          {options.length === 0 ? (
            <EmptyState
              tone="neutral"
              size="inline"
              title={t('cardBack.comments.reactions.noMatches')}
            />
          ) : (
            <EmojiGrid
              label={
                trimmedQuery === ''
                  ? t('cardBack.comments.reactions.allHeading')
                  : t('cardBack.comments.reactions.matchingHeading')
              }
              options={options}
              reacted={reacted}
              onSelect={selectEmoji}
            />
          )}
        </Popover>
      ) : null}
    </div>
  );
}

function EmojiGrid({
  label,
  options,
  reacted,
  onSelect,
}: {
  readonly label: string;
  readonly options: readonly ReactionEmoji[];
  readonly reacted: ReadonlySet<ReactionEmoji>;
  readonly onSelect: (emoji: ReactionEmoji) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  return (
    <section className="mb-3 last:mb-0" aria-label={label}>
      <h3 className="mb-1 text-xs font-medium uppercase text-ink-muted">{label}</h3>
      <div className="grid grid-cols-5 gap-1">
        {options.map((emoji) => (
          <Button
            key={emoji}
            type="button"
            size="sm"
            variant={reacted.has(emoji) ? 'secondary' : 'quiet'}
            aria-label={t('cardBack.comments.reactions.addEmoji', { emoji })}
            className="h-9 justify-center px-0 text-base"
            onClick={() => onSelect(emoji)}
          >
            {emoji}
          </Button>
        ))}
      </div>
    </section>
  );
}

function emojiSearchAlias(t: InterfaceTranslator, emoji: string): string {
  switch (emoji) {
    case '👍':
      return t('cardBack.comments.reactions.searchAlias.thumbsUp');
    case '❤️':
      return t('cardBack.comments.reactions.searchAlias.heart');
    case '😂':
      return t('cardBack.comments.reactions.searchAlias.laugh');
    case '🎉':
      return t('cardBack.comments.reactions.searchAlias.celebrate');
    case '😮':
      return t('cardBack.comments.reactions.searchAlias.surprised');
    case '😢':
      return t('cardBack.comments.reactions.searchAlias.sad');
    case '🙏':
      return t('cardBack.comments.reactions.searchAlias.thanks');
    case '👀':
      return t('cardBack.comments.reactions.searchAlias.eyes');
    case '✅':
      return t('cardBack.comments.reactions.searchAlias.done');
    case '🔥':
      return t('cardBack.comments.reactions.searchAlias.fire');
    default:
      return '';
  }
}

function readRecentReactions(): readonly ReactionEmoji[] {
  try {
    if (typeof window === 'undefined') return [];
    const value = window.localStorage.getItem(RECENT_REACTIONS_KEY);
    if (value === null) return [];
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((emoji): emoji is ReactionEmoji => typeof emoji === 'string')
      .slice(0, RECENT_REACTION_LIMIT);
  } catch {
    return [];
  }
}

function rememberRecentReaction(emoji: ReactionEmoji): readonly ReactionEmoji[] {
  const next = [
    emoji,
    ...readRecentReactions().filter((existing) => existing !== emoji),
  ].slice(0, RECENT_REACTION_LIMIT);

  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(RECENT_REACTIONS_KEY, JSON.stringify(next));
    }
  } catch {
    // Recent reactions are a convenience row only.
  }

  return next;
}
